// ExchangeManager.jsx
// Manage exchanges — view and edit market timings

import React, { useState, useEffect } from 'react';
import axios from 'axios';

import API_BASE from '../config';
import { api } from '../api';

function ExchangeManager() {
  const [exchanges, setExchanges]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(null);
  const [editMode, setEditMode]     = useState(false);
  const [form, setForm] = useState({
    exchange_code: '',
    exchange_name: '',
    market_open:   '',
    market_close:  '',
    timezone:      'Asia/Kolkata',
    is_active:     true,
  });

  useEffect(() => {
    loadExchanges();
  }, []);

  const loadExchanges = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/exchanges`);
      setExchanges(res.data);
    } catch (err) {
      setError('Failed to load exchanges');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const val = e.target.type === 'checkbox' 
                ? e.target.checked 
                : e.target.value;
    setForm({ ...form, [e.target.name]: val });
  };

  const handleEdit = (exchange) => {
    setEditMode(true);
    setForm({
      exchange_code: exchange.exchange_code,
      exchange_name: exchange.exchange_name,
      market_open:   exchange.market_open,
      market_close:  exchange.market_close,
      timezone:      exchange.timezone,
      is_active:     exchange.is_active,
    });
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.exchange_code.trim() || 
        !form.exchange_name.trim() ||
        !form.market_open.trim() || 
        !form.market_close.trim()) {
      setError('All fields except timezone are required');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/exchanges`, null, {
        params: {
          exchange_code: form.exchange_code.toUpperCase().trim(),
          exchange_name: form.exchange_name.trim(),
          market_open:   form.market_open.trim(),
          market_close:  form.market_close.trim(),
          timezone:      form.timezone.trim(),
          is_active:     form.is_active,
        }
      });
      setSuccess(
        `${form.exchange_code.toUpperCase()} saved successfully`
      );
      setEditMode(false);
      setForm({
        exchange_code: '', exchange_name: '',
        market_open:   '', market_close:  '',
        timezone:      'Asia/Kolkata', is_active: true,
      });
      await loadExchanges();
    } catch (err) {
      setError('Failed to save exchange');
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Exchange Manager</h3>

      {/* Messages */}
      {error && (
        <div style={styles.error}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={styles.closeBtn}
          >×</button>
        </div>
      )}
      {success && (
        <div style={styles.success}>
          {success}
          <button 
            onClick={() => setSuccess(null)} 
            style={styles.closeBtn}
          >×</button>
        </div>
      )}

      {/* Form */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          {editMode ? 'Edit Exchange' : 'Add Exchange'}
        </div>

        <div style={styles.formGrid}>
          <div style={styles.formField}>
            <label style={styles.label}>Exchange Code *</label>
            <input
              style={{
                ...styles.input,
                backgroundColor: editMode ? '#f5f5f5' : '#fff',
              }}
              name="exchange_code"
              placeholder="e.g. NSE"
              value={form.exchange_code}
              onChange={handleChange}
              readOnly={editMode}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Exchange Name *</label>
            <input
              style={styles.input}
              name="exchange_name"
              placeholder="e.g. National Stock Exchange"
              value={form.exchange_name}
              onChange={handleChange}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              Market Open (HH:MM) *
            </label>
            <input
              style={styles.input}
              name="market_open"
              placeholder="e.g. 09:15"
              value={form.market_open}
              onChange={handleChange}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>
              Market Close (HH:MM) *
            </label>
            <input
              style={styles.input}
              name="market_close"
              placeholder="e.g. 15:30"
              value={form.market_close}
              onChange={handleChange}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Timezone</label>
            <input
              style={styles.input}
              name="timezone"
              placeholder="e.g. Asia/Kolkata"
              value={form.timezone}
              onChange={handleChange}
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Active</label>
            <div style={styles.checkboxRow}>
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />
              <span style={styles.checkboxLabel}>
                Exchange is active
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={styles.btnPrimary}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 
             editMode ? 'Update Exchange' : 'Add Exchange'}
          </button>
          {editMode && (
            <button
              style={styles.btnSecondary}
              onClick={() => {
                setEditMode(false);
                setForm({
                  exchange_code: '', exchange_name: '',
                  market_open:   '', market_close:  '',
                  timezone:      'Asia/Kolkata', 
                  is_active:     true,
                });
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Exchange List */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>
          Exchanges ({exchanges.length})
        </div>

        {loading && (
          <div style={styles.empty}>Loading...</div>
        )}

        {!loading && exchanges.length === 0 && (
          <div style={styles.empty}>No exchanges found</div>
        )}

        {exchanges.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Open</th>
                <th style={styles.th}>Close</th>
                <th style={styles.th}>Timezone</th>
                <th style={styles.th}>Active</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {exchanges.map((ex) => (
                <tr key={ex.exchange_code} style={styles.tr}>
                  <td style={styles.tdBold}>
                    {ex.exchange_code}
                  </td>
                  <td style={styles.td}>{ex.exchange_name}</td>
                  <td style={styles.td}>
                    {ex.market_open ? ex.market_open.substring(0, 5) : '—'}
                  </td>
                  <td style={styles.td}>
                    {ex.market_close ? ex.market_close.substring(0, 5) : '—'}
                  </td>
                  <td style={styles.td}>{ex.timezone}</td>
                  <td style={styles.td}>
                    <span style={{
                      color: ex.is_active ? '#2e7d32' : '#d32f2f',
                      fontWeight: 'bold',
                    }}>
                      {ex.is_active ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button
                      style={styles.btnEdit}
                      onClick={() => handleEdit(ex)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding:   '16px',
    overflowY: 'auto',
    height:    '100%',
    background:'#f5f5f5',
    color:     '#131722',
  },
  title: {
    fontSize:     '18px',
    fontWeight:   'bold',
    marginBottom: '16px',
    color:        '#131722',
  },
  card: {
    backgroundColor: '#ffffff',
    border:          '1px solid #e0e0e0',
    borderRadius:    '8px',
    padding:         '16px',
    marginBottom:    '16px',
    boxShadow:       '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardTitle: {
    color:         '#666',
    fontSize:      '11px',
    textTransform: 'uppercase',
    marginBottom:  '12px',
    letterSpacing: '0.5px',
    fontWeight:    'bold',
  },
  formGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 '10px',
    marginBottom:        '12px',
  },
  formField: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '4px',
  },
  label: {
    fontSize: '12px',
    color:    '#666',
  },
  input: {
    padding:      '7px 10px',
    border:       '1px solid #ddd',
    borderRadius: '4px',
    fontSize:     '13px',
    color:        '#131722',
    background:   '#fff',
    width:        '100%',
    boxSizing:    'border-box',
  },
  checkboxRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    marginTop:  '4px',
  },
  checkboxLabel: {
    fontSize: '13px',
    color:    '#333',
  },
  btnPrimary: {
    padding:         '8px 20px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '13px',
    fontWeight:      'bold',
  },
  btnSecondary: {
    padding:         '8px 16px',
    backgroundColor: '#f0f0f0',
    color:           '#333',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '13px',
  },
  btnEdit: {
    padding:         '3px 8px',
    backgroundColor: '#e8f0fe',
    color:           '#2962ff',
    border:          '1px solid #c5d8fd',
    borderRadius:    '3px',
    cursor:          'pointer',
    fontSize:        '11px',
  },
  table: {
    width:          '100%',
    borderCollapse: 'collapse',
    fontSize:       '13px',
  },
  th: {
    textAlign:     'left',
    padding:       '8px 12px',
    borderBottom:  '2px solid #e0e0e0',
    color:         '#666',
    fontSize:      '11px',
    fontWeight:    'bold',
    textTransform: 'uppercase',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '8px 12px',
    color:   '#333',
  },
  tdBold: {
    padding:    '8px 12px',
    color:      '#131722',
    fontWeight: 'bold',
  },
  empty: {
    color:    '#999',
    fontSize: '13px',
    padding:  '12px 0',
  },
  error: {
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #ffcdd2',
  },
  success: {
    backgroundColor: '#f0fff4',
    color:           '#2e7d32',
    padding:         '10px 14px',
    borderRadius:    '4px',
    marginBottom:    '12px',
    fontSize:        '13px',
    display:         'flex',
    justifyContent:  'space-between',
    border:          '1px solid #c8e6c9',
  },
  closeBtn: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '16px',
    color:      'inherit',
  },
};

export default ExchangeManager;